/**
 * errorTrackingService.ts — Global error capture + backend ingestion.
 *
 * Story OBS-1: Centralized Logging + Frontend Error Capture
 *
 * Captures 3 types of errors:
 *   1. window.onerror — unhandled JS errors
 *   2. window.onunhandledrejection — unhandled promise rejections
 *   3. React ErrorBoundary — component render errors
 *   4. Axios interceptor — network errors (4xx/5xx)
 *
 * Buffers errors, deduplicates, and flushes to:
 *   POST /api/v1/system-logs/frontend-errors
 *
 * Must be initialized BEFORE React mounts (catches load-time errors).
 */

const API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || "";
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BUFFER = 50;
const DEDUP_WINDOW_MS = 5_000;
const MAX_ERRORS_PER_REQUEST = 10;
const MAX_MESSAGE_LENGTH = 2000;
const MAX_STACK_LENGTH = 10000;

export interface FrontendError {
  type: "js_error" | "unhandled_rejection" | "react_boundary" | "network_error" | "route_not_found";
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  userId?: string;
  sessionId: string;
  context?: Record<string, unknown>;
  timestamp: string;
}

class ErrorTracker {
  private buffer: FrontendError[] = [];
  private recentMessages = new Map<string, number>(); // message → timestamp
  private sessionId: string;
  private userId: string | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private initialized = false;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  /** Initialize global handlers. Call ONCE in main.tsx before React mounts. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // 1. Unhandled JS errors
    window.addEventListener("error", (event: ErrorEvent) => {
      this.captureError({
        type: "js_error",
        message: event.message || "Unknown error",
        stack: event.error?.stack,
        url: event.filename || window.location.href,
      });
    });

    // 2. Unhandled promise rejections
    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.captureError({
        type: "unhandled_rejection",
        message: reason?.message || String(reason) || "Unhandled promise rejection",
        stack: reason?.stack,
        url: window.location.href,
      });
    });

    // Auto-flush timer
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
  }

  /** Set authenticated user ID (call when AuthContext resolves). */
  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  /** Capture a frontend error. Deduplicates within 5s window. */
  captureError(partial: {
    type: FrontendError["type"];
    message: string;
    stack?: string;
    url?: string;
    context?: Record<string, unknown>;
  }): void {
    // Dedup: skip if same message within 5s
    const now = Date.now();
    const lastSeen = this.recentMessages.get(partial.message);
    if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) return;
    this.recentMessages.set(partial.message, now);

    // Clean old dedup entries
    if (this.recentMessages.size > 100) {
      for (const [msg, ts] of this.recentMessages) {
        if (now - ts > DEDUP_WINDOW_MS) this.recentMessages.delete(msg);
      }
    }

    const error: FrontendError = {
      type: partial.type,
      message: partial.message.slice(0, MAX_MESSAGE_LENGTH),
      stack: partial.stack?.slice(0, MAX_STACK_LENGTH),
      url: partial.url || window.location.href,
      userAgent: navigator.userAgent,
      userId: this.userId || undefined,
      sessionId: this.sessionId,
      context: partial.context,
      timestamp: new Date().toISOString(),
    };

    this.buffer.push(error);

    // Auto-flush if buffer full
    if (this.buffer.length >= MAX_ERRORS_PER_REQUEST) {
      this.flush();
    }

    // Cap buffer to prevent memory leak
    if (this.buffer.length > MAX_BUFFER) {
      this.buffer = this.buffer.slice(-MAX_BUFFER);
    }
  }

  /** Captura 404 de rota do React Router.
   *
   * Chamado pela página NotFound quando o usuário bate numa URL sem rota
   * registrada. Tipicamente indica link quebrado no próprio app (botão
   * antigo, redirect com UUID inválido, etc.), então vale ver em
   * /admin/erros-sistema.
   */
  captureRouteNotFound(pathname: string, referrer?: string): void {
    this.captureError({
      type: "route_not_found",
      message: `Rota não encontrada: ${pathname}`,
      context: {
        pathname,
        referrer: referrer || document.referrer || null,
        search: window.location.search || null,
      },
    });
  }

  /** Capture network error from Axios interceptor. */
  captureNetworkError(
    endpoint: string,
    statusCode: number,
    responseBody?: string,
    durationMs?: number
  ): void {
    this.captureError({
      type: "network_error",
      message: `HTTP ${statusCode} on ${endpoint}`,
      context: {
        endpoint,
        statusCode,
        responseBody: responseBody?.slice(0, 500),
        durationMs,
      },
    });
  }

  /** Flush buffered errors to backend. */
  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    const batch = this.buffer.splice(0, MAX_ERRORS_PER_REQUEST);

    try {
      // Try to get auth token for user context
      const authRaw = localStorage.getItem("seweb_auth");
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (authRaw) {
        try {
          const auth = JSON.parse(authRaw);
          if (auth.access_token) {
            headers["Authorization"] = `Bearer ${auth.access_token}`;
          }
        } catch {
          /* no auth available */
        }
      }

      // VITE_API_URL já inclui o prefixo /api/v1 (tanto em dev quanto em
      // prod via reverse proxy). Não duplicar.
      const resp = await fetch(`${API_BASE}/system-logs/frontend-errors`, {
        method: "POST",
        headers,
        body: JSON.stringify({ errors: batch }),
      });

      if (!resp.ok && resp.status !== 429) {
        // Put back if server rejected (except rate limit)
        this.buffer.unshift(...batch);
      }
    } catch {
      // Network error — put back for retry
      this.buffer.unshift(...batch);
    }
  }

  /** Cleanup on unmount. */
  destroy(): void {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flush(); // Final flush
  }

  private generateSessionId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }
}

// Singleton
export const errorTracker = new ErrorTracker();
